import { Dispatch, SetStateAction, useEffect, useState } from 'react';

type LocalStorageState<Value> = [
  value: Value,
  setValue: Dispatch<SetStateAction<Value>>,
];

export function useLocalStorage<T>(
  storageKey: string,
  initialData?: undefined,
  watch?: boolean,
): LocalStorageState<T | undefined>;
export function useLocalStorage<T>(
  storageKey: string,
  initialData: T,
  watch?: boolean,
): LocalStorageState<T>;
export function useLocalStorage<T>(
  storageKey: string,
  initialData?: T,
  watch = false,
): LocalStorageState<T | undefined> {
  const [local, setLocal] = useState<T | undefined>(() => {
    try {
      const item = localStorage.getItem(storageKey);
      return item ? JSON.parse(item) : initialData;
    } catch {
      return initialData;
    }
  });

  useEffect(() => {
    if (local === undefined) {
      localStorage.removeItem(storageKey);
    } else {
      const serialized = JSON.stringify(local);
      const current = localStorage.getItem(storageKey);
      // prevent infinite update loop if storage is being watched
      if (current !== serialized) {
        localStorage.setItem(storageKey, serialized);
      }
    }
  }, [local, storageKey]);

  useEffect(() => {
    if (!watch) return undefined;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === storageKey) {
        if (event.newValue === null) {
          setLocal(undefined);
        } else {
          try {
            const newValue = JSON.parse(event.newValue);
            setLocal(newValue);
          } catch {
            // ignore parse errors
          }
        }
      }
    };

    globalThis.addEventListener('storage', handleStorageChange);
    return () => globalThis.removeEventListener('storage', handleStorageChange);
  }, [storageKey, watch]);

  return [local, setLocal];
}
